import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { Link } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import { sendResetPasswordLinkSchema, SendResetPasswordLinkRequest } from '$/requests/SendResetPasswordLink.ts';
import Routes from '@/router/Routes.ts';

const INPUT_LABEL_CLASS =
  '!bg-[#FAFAFA] !border !border-[rgba(204,204,204,0.5)] !rounded-xl !min-h-[48px] font-dm text-sm';

const ForgotPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<SendResetPasswordLinkRequest>({
    resolver: zodResolver(sendResetPasswordLinkSchema),
    defaultValues: {
      email: '',
    },
  });
  const {
    sendResetPasswordLinkQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const isLoading = isLoadingResetPassword;

  const handleSendResetPasswordLink = (data: SendResetPasswordLinkRequest) => {
    void (async () => {
      try {
        if (isLoading) return;

        await resetPassword({
          email: data.email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent centerContent>
      <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit(handleSendResetPasswordLink)}>
        <div className="flex flex-col gap-2 py-6">
          <h1 className="font-newsreader font-bold text-2xl leading-[117%] text-[rgba(0,0,0,0.87)]">
            {t('forgotPasswordPage.header')}
          </h1>
          <p className="font-dm text-sm font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            {t('forgotPasswordPage.subtitle')}
          </p>
        </div>
        {isSuccessResetPassword && <Alert status="success">{t('forgotPasswordPage.successInfo')}</Alert>}
        {isErrorResetPassword && <Alert status="error">{errorResetPassword?.message}</Alert>}
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value }, formState: { errors } }) => (
            <TextInput
              error={errors.email?.message}
              value={value}
              onChange={onChange}
              disabled={isSuccessResetPassword}
              placeholder={t('label.email')}
              labelClassName={INPUT_LABEL_CLASS}
            />
          )}
        />
        <LoadingButton
          loading={isLoading}
          disabled={isSuccessResetPassword}
          type="submit"
          color="primary"
          className="w-full"
        >
          {t('button.send')}
        </LoadingButton>
        <div className="flex items-center justify-between">
          <Link className="font-dm font-semibold text-sm text-[#122346]" to={Routes.LOGIN}>
            {t('button.goBack')}
          </Link>
          <Link className="font-dm font-semibold text-sm text-[#122346]" to={Routes.REGISTER}>
            {t('button.createAccount')}
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default ForgotPasswordPage;
